/**
 * Currency Service - Refactored with Unified Architecture
 * Migrated from axios to fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { Rate } from "@/types/currency.type";

import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";

// Enhanced type definitions for currency responses
export interface CurrencyRatesResponse {
  rates: Record<string, number>;
  base: string;
  date: string;
  success: boolean;
}

export interface CurrencyConversionResponse {
  amount: number;
  from: string;
  to: string;
  rate: number;
  convertedAmount: number;
  timestamp: string;
}

/**
 * Currency Service Class
 * Handles all currency-related operations
 * Single Responsibility: Currency rate management and conversion
 */
class CurrencyService extends BaseService {
  constructor() {
    super({
      name: "currency",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 3, // Currency rates can benefit from retries
      retryDelay: 2000,
    });
  }

  /**
   * Get currency exchange rates
   * @param currencyFrom - Base currency code (e.g., "USD")
   * @param currencyTo - Array of target currency codes (e.g., ["EUR", "GBP"])
   */
  async getCurrencyRates({
    currencyFrom,
    currencyTo,
  }: Required<Rate>): Promise<CurrencyRatesResponse> {
    this.validateRequired({ currencyFrom, currencyTo }, [
      "currencyFrom",
      "currencyTo",
    ]);

    if (!Array.isArray(currencyTo) || currencyTo.length === 0) {
      this.handleError(
        {
          message: "currencyTo must be a non-empty array",
          code: "INVALID_CURRENCY_TO",
          details: { currencyTo },
        },
        "currency rates validation",
      );
    }

    // Join target currencies as comma-separated string
    const joinCurrencyTo = currencyTo.join(",");

    const queryParams = {
      target: joinCurrencyTo,
      base: currencyFrom,
    };

    try {
      const response = await this.get<CurrencyRatesResponse>(
        "/currency/rates",
        queryParams,
      );

      this.logOperationSuccess("get currency rates", {
        base: currencyFrom,
        targets: currencyTo.length,
        hasRates: !!response.rates,
      });

      return response;
    } catch (error) {
      this.handleError(error, "get currency rates");
    }
  }

  /**
   * Convert amount between currencies
   * @param amount - Amount to convert
   * @param from - Source currency code
   * @param to - Target currency code
   */
  async convertCurrency(
    amount: number,
    from: string,
    to: string,
  ): Promise<CurrencyConversionResponse> {
    this.validateRequired({ amount, from, to }, ["amount", "from", "to"]);

    if (amount <= 0) {
      this.handleError(
        {
          message: "Amount must be greater than 0",
          code: "INVALID_AMOUNT",
          details: { amount },
        },
        "currency conversion validation",
      );
    }

    const queryParams = {
      amount: amount.toString(),
      from,
      to,
    };

    try {
      const response = await this.get<CurrencyConversionResponse>(
        "/currency/convert",
        queryParams,
      );

      this.logOperationSuccess("convert currency", {
        amount,
        from,
        to,
        convertedAmount: response.convertedAmount,
      });

      return response;
    } catch (error) {
      this.handleError(error, "convert currency");
    }
  }

  /**
   * Get all supported currencies
   */
  async getSupportedCurrencies(): Promise<{
    currencies: Array<{
      code: string;
      name: string;
      symbol: string;
    }>;
  }> {
    try {
      const response = await this.get<{
        currencies: Array<{
          code: string;
          name: string;
          symbol: string;
        }>;
      }>("/currency/supported");

      this.logOperationSuccess("get supported currencies", {
        count: response.currencies.length,
      });

      return response;
    } catch (error) {
      this.handleError(error, "get supported currencies");
    }
  }

  /**
   * Get historical exchange rates
   * @param currencyFrom - Base currency code
   * @param currencyTo - Target currency code
   * @param date - Date in YYYY-MM-DD format
   */
  async getHistoricalRates(
    currencyFrom: string,
    currencyTo: string,
    date: string,
  ): Promise<CurrencyRatesResponse> {
    this.validateRequired({ currencyFrom, currencyTo, date }, [
      "currencyFrom",
      "currencyTo",
      "date",
    ]);

    // Validate date format
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      this.handleError(
        {
          message: "Date must be in YYYY-MM-DD format",
          code: "INVALID_DATE_FORMAT",
          details: { date },
        },
        "historical rates validation",
      );
    }

    const queryParams = {
      base: currencyFrom,
      target: currencyTo,
      date,
    };

    try {
      const response = await this.get<CurrencyRatesResponse>(
        "/currency/historical",
        queryParams,
      );

      this.logOperationSuccess("get historical rates", {
        base: currencyFrom,
        target: currencyTo,
        date,
      });

      return response;
    } catch (error) {
      this.handleError(error, "get historical rates");
    }
  }

  /**
   * Validate currency code format
   */
  private isValidCurrencyCode(code: string): boolean {
    // ISO 4217 currency codes are 3 uppercase letters
    const currencyRegex = /^[A-Z]{3}$/;
    return currencyRegex.test(code);
  }

  /**
   * Enhanced validation for currency data
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // First run base validation
    super.validateRequired(params, requiredFields);

    // Additional currency-specific validation
    if (params.currencyFrom && !this.isValidCurrencyCode(params.currencyFrom)) {
      this.handleError(
        {
          message:
            "Invalid currency code format. Must be 3 uppercase letters (e.g., USD)",
          code: "INVALID_CURRENCY_FROM",
          details: { currencyFrom: params.currencyFrom },
        },
        "currency from validation",
      );
    }

    if (params.currencyTo) {
      if (Array.isArray(params.currencyTo)) {
        const invalidCodes = params.currencyTo.filter(
          (code: string) => !this.isValidCurrencyCode(code),
        );
        if (invalidCodes.length > 0) {
          this.handleError(
            {
              message:
                "Invalid currency codes found. Must be 3 uppercase letters",
              code: "INVALID_CURRENCY_TO",
              details: { invalidCodes },
            },
            "currency to validation",
          );
        }
      } else if (
        typeof params.currencyTo === "string" &&
        !this.isValidCurrencyCode(params.currencyTo)
      ) {
        this.handleError(
          {
            message:
              "Invalid currency code format. Must be 3 uppercase letters",
            code: "INVALID_CURRENCY_TO",
            details: { currencyTo: params.currencyTo },
          },
          "currency to validation",
        );
      }
    }

    if (params.from && !this.isValidCurrencyCode(params.from)) {
      this.handleError(
        {
          message: "Invalid 'from' currency code format",
          code: "INVALID_FROM_CURRENCY",
          details: { from: params.from },
        },
        "from currency validation",
      );
    }

    if (params.to && !this.isValidCurrencyCode(params.to)) {
      this.handleError(
        {
          message: "Invalid 'to' currency code format",
          code: "INVALID_TO_CURRENCY",
          details: { to: params.to },
        },
        "to currency validation",
      );
    }

    if (
      params.amount !== undefined &&
      (typeof params.amount !== "number" || params.amount <= 0)
    ) {
      this.handleError(
        {
          message: "Amount must be a positive number",
          code: "INVALID_AMOUNT",
          details: { amount: params.amount },
        },
        "amount validation",
      );
    }
  }

  /**
   * Custom health check for currency service
   */
  async healthCheck() {
    try {
      // Simple health check by getting USD to EUR rate
      await this.getCurrencyRates({
        currencyFrom: "USD",
        currencyTo: ["EUR"],
      });

      return {
        service: this.serviceName,
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          rates: "healthy",
          conversion: "healthy",
        },
      };
    } catch (error) {
      return {
        service: this.serviceName,
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }
}

// Export singleton instance
export const currencyService = new CurrencyService();

// Export for backward compatibility
export default currencyService;

// Export types for external use
export type { CurrencyRatesResponse, CurrencyConversionResponse };
